import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { Variant } from '../models/variant';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-variants',
    templateUrl: './developer-variants.component.html',
    styleUrls: ['./developer-variants.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperVariantsComponent implements OnInit, AfterViewInit {
    variants: Variant[] = [];
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;

    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) { }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_Variant_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_Variant_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_Variant_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.variants = [];

            this.variants = await this.developerService.getVariants();
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = { VariantsId: null, VariantName: null } as Variant;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Variant, item => this.saveVariant(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as Variant, item => this.saveVariant(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as Variant, item => this.removeVariant(item));
    };

    async saveVariant(variant: Variant): Promise<Variant> {
        const variantExist = this.variants.find(i => i.VariantName === variant.VariantName && i.VariantsId !== variant.VariantsId);
        if (variantExist != null) {
            throw new Error(`Another variant with that name, already exists. Name of the existing variant: ${variantExist.VariantName}`);
        }

        const newVariant = await this.developerService.saveVariant(variant);
        this.toastService.showToast('Variant saved...', ToastType.success);
        return newVariant;
    }

    async removeVariant(variant: Variant): Promise<Variant> {
        await this.developerService.deleteVariant(variant.VariantsId);
        this.toastService.showToast('Variant deleted...', ToastType.success);
        return variant;
    }
}
